/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { getFacturasRecibidas, getFacturasPagadasViaEdificio } from "../../utils/conciliacionSII";
import { FECHA_CORTE } from "../../utils/estadoCuenta";
import AsignarRecibidaModal from "../ConciliacionSII/AsignarRecibidaModal";
import styles from "./VentasFacturas.module.scss";

// ─── Helpers ───────────────────────────────────────────────

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  let s = fechaStr;
  if (s.includes("T")) s = s.substring(0, 10);
  const [, month, day] = s.split("-");
  return `${day}/${month}`;
};

const TIPO_DTE_LABEL = {
  33: "Factura",
  34: "Exenta",
  39: "Boleta",
  41: "Boleta exenta",
  56: "Nota débito",
  61: "Nota crédito",
  110: "Exportación",
  111: "Liquidación exp.",
  112: "Nota débito exp.",
};

const ESTADO_CHIP = {
  pendiente: { label: "Pendiente", bg: "#fdebd0", color: "#a66300" },
  vinculada: { label: "Vinculada", bg: "#d5f5e3", color: "#1e8449" },
  ignorada: { label: "Ignorada", bg: "#e5e7e9", color: "#707b7c" },
  // Display-only (read-time, reglas 10.70/10.71): la factura SII quedó atada a un movimiento
  // bancario vía un pago a edificio. Es un matiz de "Vinculada" (no un tercer estado): el
  // vínculo vino de un pago a edificio, no de proveedor común. estado_vinculacion NO se toca en
  // DB (puede seguir 'pendiente'). Turquesa para distinguirlo del Vinculada normal (regla 10.72).
  pagada_edificio: { label: "Vinculada (edificio)", bg: "#d1f2eb", color: "#138d75" },
};

const CUENTAS_ER_MAP = Object.fromEntries(CUENTAS_ER.map((c) => [c.value, c.label]));

// ─── Componente principal ─────────────────────────────────

const ComprasTab = ({ pais, meses, filtroEstado, busqueda, filtroCuentaER }) => {
  const [facturas, setFacturas] = useState([]);
  const [pagadasEdificio, setPagadasEdificio] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [modalFactura, setModalFactura] = useState(null);

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      // Sin meses seleccionados → todas las recibidas desde el corte contable.
      const filtros = meses?.length
        ? { periodos: meses, estado: filtroEstado || undefined }
        : { fechaDesde: FECHA_CORTE, estado: filtroEstado || undefined };
      const [data, pagadasSet] = await Promise.all([
        getFacturasRecibidas(pais, filtros),
        getFacturasPagadasViaEdificio(pais),
      ]);
      setFacturas(data);
      setPagadasEdificio(pagadasSet);
    } catch (err) {
      console.error("Error cargando facturas recibidas:", err);
      toast.error("Error cargando facturas recibidas");
    } finally {
      setLoading(false);
    }
  }, [pais, meses, filtroEstado]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const facturasFiltradas = useMemo(() => {
    const q = (busqueda || "").trim().toLowerCase();
    if (!q) return facturas;
    return facturas.filter(
      (f) => (f.razon_social_emisor || "").toLowerCase().includes(q) || (f.rut_emisor || "").toLowerCase().includes(q)
    );
  }, [facturas, busqueda]);

  // Filtro de Cuenta ER: solo recorta las filas visibles de la tabla. Las KPI cards (resumen)
  // siguen derivando de facturasFiltradas (mes+estado+búsqueda), no de este array.
  const facturasVisibles = useMemo(() => {
    if (!filtroCuentaER) return facturasFiltradas;
    if (filtroCuentaER === "__sin__") return facturasFiltradas.filter((f) => !f.cuenta_er);
    return facturasFiltradas.filter((f) => f.cuenta_er === filtroCuentaER);
  }, [facturasFiltradas, filtroCuentaER]);

  const resumen = useMemo(() => {
    // NCs (tipo_dte=61) reducen deuda: no cuentan como DTE pendiente y netean en monto.
    const esNC = (f) => f.tipo_dte === 61;
    const sinNC = facturasFiltradas.filter((f) => !esNC(f));
    const total = sinNC.length;
    // Las pagadas vía edificio NO son deuda pendiente (su pago ya existe; la factura es soporte,
    // reglas 10.70/10.71). Se descuentan del conteo PENDIENTES igual que del chip de la tabla.
    const pendientes = sinNC.filter((f) => f.estado_vinculacion === "pendiente" && !pagadasEdificio.has(f.id));
    const gastos = facturasFiltradas.filter((f) => f.estado_vinculacion === "vinculada" && f.tipo_contable === "gasto");
    const activos = facturasFiltradas.filter(
      (f) => f.estado_vinculacion === "vinculada" && f.tipo_contable === "activo"
    );

    // Signo por tipo_dte (consistente con calcularImpuestos.js / iva.js): la NC resta.
    const sumarNeto = (arr) =>
      arr.reduce((sum, f) => {
        const signo = esNC(f) ? -1 : 1;
        const neto =
          Number(f.monto_neto) === 0 && Number(f.monto_exento) > 0 ? Number(f.monto_exento) : Number(f.monto_neto || 0);
        return sum + signo * neto;
      }, 0);

    const totalIva = facturasFiltradas.reduce((sum, f) => sum + (esNC(f) ? -1 : 1) * Number(f.monto_iva || 0), 0);

    return {
      total,
      totalNeto: sumarNeto(facturasFiltradas),
      totalIva,
      pendientes: { count: pendientes.length, monto: sumarNeto(pendientes) },
      gastos: { count: gastos.length, monto: sumarNeto(gastos) },
      activos: { count: activos.length, monto: sumarNeto(activos) },
    };
  }, [facturasFiltradas, pagadasEdificio]);

  return (
    <div className={styles.containerFacturasDetalle} style={{ paddingTop: 0 }}>
      {/* Cards resumen */}
      <div className={styles.cardsSectionCompras}>
        <div className={styles.card}>
          <div className={styles.cardLabel}>TOTAL DTES ({resumen.total})</div>
          <div style={{ marginTop: 5 }}>
            <span className={styles.cardValue}>{formatMonto(resumen.totalNeto)}</span>
          </div>
        </div>
        <div className={`${styles.card} ${resumen.pendientes.count > 0 ? styles.cardRed : ""}`}>
          <div className={styles.cardLabel}>PENDIENTES ({resumen.pendientes.count})</div>
          <div style={{ marginTop: 5 }}>
            <span className={styles.cardValue}>{formatMonto(resumen.pendientes.monto)}</span>
          </div>
        </div>
        <div className={`${styles.card} ${resumen.gastos.count > 0 ? styles.cardGreen : ""}`}>
          <div className={styles.cardLabel}>GASTOS ({resumen.gastos.count})</div>
          <div style={{ marginTop: 5 }}>
            <span className={styles.cardValue}>{formatMonto(resumen.gastos.monto)}</span>
          </div>
        </div>
        <div className={`${styles.card} ${resumen.activos.count > 0 ? styles.cardBlue : ""}`}>
          <div className={styles.cardLabel}>ACTIVOS ({resumen.activos.count})</div>
          <div style={{ marginTop: 5 }}>
            <span className={styles.cardValue}>{formatMonto(resumen.activos.monto)}</span>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>IVA MES</div>
          <div style={{ marginTop: 5 }}>
            <span className={styles.cardValue}>{formatMonto(resumen.totalIva)}</span>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>Cargando...</div>
      ) : facturasVisibles.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
          No hay facturas recibidas para este período
        </div>
      ) : (
        <div className={styles.tableResponsive}>
          <table className={styles.table}>
            <thead className={styles.stickyHead}>
              <tr>
                <th className={styles.colCmpFecha}>Fecha</th>
                <th className={styles.colCmpProveedor} style={{ textAlign: "left" }}>
                  Proveedor
                </th>
                <th className={styles.colCmpRut}>RUT</th>
                <th className={styles.colCmpTipoDte} style={{ textAlign: "left" }}>
                  Tipo DTE
                </th>
                <th className={styles.colCmpFolio}>Folio</th>
                <th className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                  Neto
                </th>
                <th className={styles.colCmpIva} style={{ textAlign: "right" }}>
                  IVA
                </th>
                <th className={styles.colCmpTotal} style={{ textAlign: "right" }}>
                  Total
                </th>
                <th className={styles.colCmpTipo}>Tipo</th>
                <th className={styles.colCmpCuentaEr} style={{ textAlign: "left" }}>
                  Cuenta ER
                </th>
                <th className={styles.colCmpEstado}>Estado</th>
                <th className={styles.colCmpAccion}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {facturasVisibles.map((f) => {
                // "Pagada (edificio)" tiene prioridad: su estado_vinculacion sigue 'pendiente'
                // en DB (10.71), pero el pago a edificio ya existe → no mentir "Pendiente".
                const chip = pagadasEdificio.has(f.id)
                  ? ESTADO_CHIP.pagada_edificio
                  : ESTADO_CHIP[f.estado_vinculacion] || ESTADO_CHIP.pendiente;
                const cuentaLabel = f.cuenta_er ? CUENTAS_ER_MAP[f.cuenta_er] || f.cuenta_er : "—";
                const vinculada = f.estado_vinculacion === "vinculada";
                return (
                  <tr key={f.id} onClick={() => setModalFactura(f)} style={{ cursor: "pointer" }}>
                    <td className={styles.colCmpFecha} style={{ textAlign: "center" }}>
                      {formatFecha(f.fecha_emision)}
                    </td>
                    <td className={styles.colCmpProveedor}>{f.razon_social_emisor || "—"}</td>
                    <td className={styles.colCmpRut} style={{ textAlign: "center" }}>
                      {formatRut(f.rut_emisor)}
                    </td>
                    <td className={styles.colCmpTipoDte}>{TIPO_DTE_LABEL[f.tipo_dte] || f.tipo_dte}</td>
                    <td className={styles.colCmpFolio} style={{ textAlign: "center" }}>
                      {f.folio}
                    </td>
                    <td className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                      {Number(f.monto_neto) === 0 && Number(f.monto_exento) > 0
                        ? formatMonto(f.monto_exento)
                        : formatMonto(f.monto_neto)}
                    </td>
                    <td className={styles.colCmpIva} style={{ textAlign: "right" }}>
                      {formatMonto(f.monto_iva)}
                    </td>
                    <td className={styles.colCmpTotal} style={{ textAlign: "right", fontWeight: 600 }}>
                      {formatMonto(f.monto_total)}
                    </td>
                    <td className={styles.colCmpTipo} style={{ textAlign: "center" }}>
                      {vinculada && f.tipo_contable ? (
                        <span style={{ textTransform: "capitalize", color: "#555" }}>{f.tipo_contable}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={styles.colCmpCuentaEr}>{vinculada ? cuentaLabel : "—"}</td>
                    <td className={styles.colCmpEstado} style={{ textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          background: chip.bg,
                          color: chip.color,
                        }}
                      >
                        {chip.label}
                      </span>
                    </td>
                    <td className={styles.colCmpAccion} style={{ textAlign: "center" }}>
                      <FaEdit
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalFactura(f);
                        }}
                        style={{ cursor: "pointer", fontSize: "1.2em", color: "#2c3e50" }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AsignarRecibidaModal
        isOpen={!!modalFactura}
        toggle={() => setModalFactura(null)}
        factura={modalFactura}
        onAsignada={() => {
          setModalFactura(null);
          cargarDatos();
        }}
      />
    </div>
  );
};

export default ComprasTab;
